import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { useTheme } from '@/contexts/ThemeContext';
import { useUsage } from '@/contexts/UsageContext';
import { useGuestDemo } from '@/contexts/GuestDemoContext';
import { useState } from 'react';
import Pressable from '@/components/base/Pressable';
import PremiumGate from '@/components/feature/PremiumGate';
import DemoGate from '@/components/feature/DemoGate';

export default function ScanOutput() {
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  const { checkUsage, recordUsage, isLoading, accessError } = useUsage();
  const { canScan, canVideoPreview, useVideoPreview, activeGate, dismissGate } = useGuestDemo();
  const isDark = resolvedTheme === 'dark';

  const [showGate, setShowGate] = useState(false);
  const [customInstruction, setCustomInstruction] = useState('');
  const [uploadedImage] = useState(() => sessionStorage.getItem('scan_input_image') || '');

  const consumeVideoPreview = useVideoPreview;

  const videoStatus = checkUsage('video_generate');

  const getInstruction = (format: 'text' | 'video') => (
    customInstruction.trim() || (
      format === 'video'
        ? 'Create a clear, guided video lesson from this question and teach the working step by step.'
        : 'Create a clear written solution from this question and show the essential working step by step.'
    )
  );

  const t = {
    pageBg: isDark ? 'bg-[#0F0F10]' : 'bg-white',
    textPrimary: isDark ? 'text-white' : 'text-neutral-900',
    textSecondary: isDark ? 'text-neutral-400' : 'text-neutral-500',
    textMuted: isDark ? 'text-neutral-500' : 'text-neutral-400',
    textFaint: isDark ? 'text-neutral-700' : 'text-neutral-300',
    iconBg: isDark ? 'bg-white/[0.04]' : 'bg-neutral-100',
    accentBg: isDark ? 'bg-neutral-800' : 'bg-neutral-100',
    divider: isDark ? 'border-white/[0.04]' : 'border-neutral-100',
    lowBadge: isDark ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' : 'bg-amber-50 text-amber-600 border-amber-200',
    emptyBadge: isDark ? 'bg-red-500/10 text-red-400 border-red-500/20' : 'bg-red-50 text-red-600 border-red-200',
    adBadge: isDark ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-emerald-50 text-emerald-600 border-emerald-200',
  };

  const proceedWithVideo = () => {
    sessionStorage.removeItem('scan_help_mode');
    sessionStorage.setItem('scan_instruction', getInstruction('video'));
    consumeVideoPreview();
    recordUsage('video_generate');
    sessionStorage.setItem('scan_output_mode', 'video');
    navigate('/scan/analyzing');
  };

  const handleTextSelect = () => {
    sessionStorage.removeItem('scan_help_mode');
    sessionStorage.setItem('scan_instruction', getInstruction('text'));
    sessionStorage.setItem('scan_output_mode', 'text');
    navigate('/scan/analyzing');
  };

  const handleVideoSelect = () => {
    if (!canScan) {
      dismissGate();
      return;
    }
    if (!videoStatus.allowed) {
      // Only server usage/entitlement records can authorize a generation.
      // Watching a client-side countdown cannot add quota or grant Premium.
      if (!isLoading && !accessError) setShowGate(true);
      return;
    }
    proceedWithVideo();
  };

  return (
    <div className={`flex flex-col min-h-full ${t.pageBg} transition-colors duration-500`}>
      {/* Header */}
      <div className="px-5 pt-8 pb-5 flex items-center gap-3">
        <Pressable
          onClick={() => navigate(-1)}
          className={`w-9 h-9 flex items-center justify-center rounded-xl ${t.iconBg} transition-colors duration-500`}
          scale={0.88}
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className={`ri-arrow-left-line ${t.textMuted}`} />
          </div>
        </Pressable>
        <div>
          <p className={`text-[11px] font-medium ${t.textMuted} uppercase tracking-wider`}>Step 2 of 3</p>
          <h1 className={`text-lg font-bold leading-tight ${t.textPrimary}`}>Choose Format</h1>
        </div>
      </div>

      {/* Prompt */}
      <motion.div
        className="px-5 pb-8"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.32, 0.72, 0, 1] }}
      >
        <div className={`rounded-2xl border ${t.divider} p-5 ${isDark ? 'bg-white/[0.02]' : 'bg-neutral-50'} transition-colors duration-500`}>
           <div className="flex items-center gap-3">
             {uploadedImage.startsWith('data:image/') ? (
               <img
                 src={uploadedImage}
                 alt="Uploaded question"
                 className="w-14 h-14 rounded-xl object-cover border border-black/10 dark:border-white/10"
               />
             ) : (
               <div className={`w-14 h-14 rounded-xl ${t.accentBg} flex items-center justify-center`}>
                 <i className={`ri-file-text-line ${t.textMuted} text-xl`} />
               </div>
             )}
             <div className="min-w-0">
               <p className={`text-sm font-semibold ${t.textPrimary}`}>Uploaded/Taken Question</p>
               <p className={`text-[11px] ${t.textMuted} mt-1`}>Ready to generate your study material</p>
             </div>
           </div>
        </div>
      </motion.div>

       {/* Generation instructions and output options */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="px-5 pb-5"
      >
        <div className={`rounded-xl border ${t.divider} ${isDark ? 'bg-white/[0.03]' : 'bg-neutral-50'} px-3 py-2.5 flex items-start gap-2`}>
          <i className={`ri-chat-3-line text-sm mt-0.5 ${t.textMuted}`} />
          <textarea
            value={customInstruction}
            onChange={(event) => setCustomInstruction(event.target.value)}
             placeholder="Type something... e.g. Focus on question 2 or use an easier method."
            rows={2}
            className={`flex-1 bg-transparent outline-none resize-none text-[11px] leading-relaxed ${t.textPrimary}`}
          />
        </div>
         <div className="grid grid-cols-2 gap-3 mt-3">
           <Pressable
             onClick={handleTextSelect}
             className={`min-h-12 rounded-xl border ${isDark ? 'border-white/[0.12] bg-white/[0.07] hover:bg-white/[0.11]' : 'border-neutral-200 bg-white hover:bg-neutral-50'} px-3 py-3 flex items-center justify-center gap-2 transition-colors`}
             scale={0.97}
           >
             <i className={`ri-file-text-line ${t.textPrimary} text-base`} />
             <span className={`text-xs font-bold ${t.textPrimary}`}>Generate Text</span>
           </Pressable>
           <Pressable
             onClick={handleVideoSelect}
             className="min-h-12 rounded-xl bg-neutral-900 px-3 py-3 flex items-center justify-center gap-2 transition-colors hover:bg-neutral-800"
             scale={0.97}
           >
             <i className="ri-video-line text-white text-base" />
             <span className="text-xs font-bold text-white">Generate Video</span>
           </Pressable>
         </div>
         {!videoStatus.allowed && (
           <p className={`text-[10px] ${t.textMuted} text-center mt-2`}>
              {isLoading ? 'Verifying generation access…' : accessError || 'Your server-verified generation limit has been reached. Please wait for it to reset.'}
           </p>
         )}
      </motion.div>

      {/* Bottom hint */}
      <motion.div
        className="px-5 pb-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6, duration: 0.4 }}
      >
        <p className={`text-[11px] ${t.textMuted} text-center leading-relaxed`}>
           Your generated material is saved to History automatically.
        </p>
      </motion.div>

      <PremiumGate
        feature="video_generate"
        open={showGate}
        onClose={() => setShowGate(false)}
      />

      <DemoGate
        feature="scan"
        open={!!activeGate}
        onClose={dismissGate}
      />
    </div>
  );
}